import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton, StyledSegmentedControl, StyledSegmentButton } from './SectionBar.styled';

interface CN10ReportFiltersProps {
  customFilters: {
    mailCategories?: string[];
    fromPeriod?: string;
    toPeriod?: string;
    type?: string;
    onMailCategoriesChange?: (values: string[]) => void;
    onFromPeriodChange?: (period: string) => void;
    onToPeriodChange?: (period: string) => void;
    onTypeChange?: (type: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
    yearQuarterOptions?: Array<{ value: string; label: string; year: string; quarter: string }>;
  };
}

const CN10ReportFilters: React.FC<CN10ReportFiltersProps> = ({ customFilters }) => {
  // State for dropdown menus
  const [mailCategoriesAnchorEl, setMailCategoriesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [fromPeriodAnchorEl, setFromPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toPeriodAnchorEl, setToPeriodAnchorEl] = React.useState<null | HTMLElement>(null);

  const isMailCategoriesMenuOpen = Boolean(mailCategoriesAnchorEl);
  const isFromPeriodMenuOpen = Boolean(fromPeriodAnchorEl);
  const isToPeriodMenuOpen = Boolean(toPeriodAnchorEl);

  // Generate year-quarter options for CN10Report (from 2008 to current year)
  // Use cascaded options from parent if provided, otherwise generate all options
  const yearQuarterOptions = React.useMemo(() => {
    // If parent provides cascaded options, use them
    if (customFilters.yearQuarterOptions && customFilters.yearQuarterOptions.length > 0) {
      return customFilters.yearQuarterOptions;
    }
    
    // Otherwise, generate all options
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= 2008; year--) { // Reverse order to show recent years first
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({
          value,
          label,
          year: year.toString(),
          quarter
        });
      });
    }
    
    return options;
  }, [customFilters.yearQuarterOptions]);

  // Filter options for CN10 (only A and D)
  const mailCategoryOptions = [
    { id: 'A', label: 'A - Airmail Priority' },
    { id: 'D', label: 'D - Surface Priority' },
  ];

  // Type filter options (Inbound/Outbound)
  const typeOptions = [
    { id: 'inbound', label: 'Inbound' },
    { id: 'outbound', label: 'Outbound' },
  ];

  // Handlers
  const handleMailCategoriesClick = (event: React.MouseEvent<HTMLElement>) => {
    setMailCategoriesAnchorEl(event.currentTarget);
  };

  const handleMailCategoriesMenuClose = () => {
    setMailCategoriesAnchorEl(null);
  };

  const handleFromPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setFromPeriodAnchorEl(event.currentTarget);
  };

  const handleFromPeriodMenuClose = () => {
    setFromPeriodAnchorEl(null);
  };

  const handleFromPeriodChange = (period: string) => {
    customFilters.onFromPeriodChange?.(period);
    handleFromPeriodMenuClose();
  };

  const handleToPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setToPeriodAnchorEl(event.currentTarget);
  };

  const handleToPeriodMenuClose = () => {
    setToPeriodAnchorEl(null);
  };

  const handleToPeriodChange = (period: string) => {
    customFilters.onToPeriodChange?.(period);
    handleToPeriodMenuClose();
  };

  const handleMailCategoryToggle = (value: string) => {
    const currentValues = customFilters.mailCategories || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onMailCategoriesChange?.(newValues);
  };

  const handleTypeChange = (type: string) => {
    customFilters.onTypeChange?.(type);
  };

  // Display labels
  const getFromPeriodLabel = () => {
    if (!customFilters.fromPeriod) return 'Select From';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.fromPeriod);
    return option ? option.label : 'Select From';
  };

  const getToPeriodLabel = () => {
    if (!customFilters.toPeriod) return 'Select To';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.toPeriod);
    return option ? option.label : 'Select To';
  };

  const getMailCategoriesLabel = () => {
    const selected = customFilters.mailCategories || [];
    if (selected.length === 0) return 'Select Mail Categories';
    if (selected.length === mailCategoryOptions.length) return 'All Mail Categories';
    return `${selected.length} Mail Categories`;
  };

  return (
    <>
      {/* From Period Filter */}
      <FilterButton onClick={handleFromPeriodClick}>
        <span className='label'>From YYYY.QQ</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getFromPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={fromPeriodAnchorEl}
        open={isFromPeriodMenuOpen}
        onClose={handleFromPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleFromPeriodChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleFromPeriodChange(option.value)}
            selected={customFilters.fromPeriod === option.value}
            sx={{
              fontWeight: customFilters.fromPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* To Period Filter */}
      <FilterButton onClick={handleToPeriodClick}>
        <span className='label'>To YYYY.QQ</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getToPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={toPeriodAnchorEl}
        open={isToPeriodMenuOpen}
        onClose={handleToPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleToPeriodChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleToPeriodChange(option.value)}
            selected={customFilters.toPeriod === option.value}
            sx={{
              fontWeight: customFilters.toPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Type Filter (Inbound/Outbound) */}
      <StyledSegmentedControl sx={{ mr: 2 }}>
        {typeOptions.map((option) => (
          <StyledSegmentButton
            key={option.id}
            active={customFilters.type === option.id}
            onClick={() => handleTypeChange(option.id)}
            sx={{ 
              color: customFilters.type === option.id ? '#ffffff' : '#00B6BA',
              minWidth: '80px'
            }}
          >
            {option.label}
          </StyledSegmentButton>
        ))}
      </StyledSegmentedControl>

      {/* Mail Categories Filter */}
      <FilterButton onClick={handleMailCategoriesClick}>
        <span className='label'>Mail Category</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getMailCategoriesLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={mailCategoriesAnchorEl}
        open={isMailCategoriesMenuOpen}
        onClose={handleMailCategoriesMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '220px',
          },
        }}
      >
        {mailCategoryOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleMailCategoryToggle(option.id)}
            sx={{
              padding: '8px 16px',
              fontSize: '14px',
            }}
          >
            <Checkbox
              checked={customFilters.mailCategories?.includes(option.id) || false}
              size="small"
              sx={{ mr: 1, padding: 0 }}
            />
            <Typography sx={{ fontSize: '14px' }}>{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Export Buttons */}
      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('HTML')}
          disabled={customFilters.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'HTML'}
          </Typography>
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('PDF')}
          disabled={customFilters.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'PDF'}
          </Typography>
        </StyledActionButton>

      </Box>
    </>
  );
};

export default CN10ReportFilters; 